import React from 'react';
import { useSpc } from '../../context/SpcContext';
import { Image as ImageIcon, X, MessageCircle, Pin, Trash2, Send, Smile } from 'lucide-react';
import GifPicker from '../GifPicker';

const REACTIONS = [
  { key: 'like', emoji: '👍', label: 'Like', color: '#2d88ff' },
  { key: 'love', emoji: '❤️', label: 'Love', color: '#e1e20a' },
  { key: 'haha', emoji: '😆', label: 'Haha', color: '#f7b125' },
  { key: 'wow', emoji: '😮', label: 'Wow', color: '#f7b125' },
  { key: 'sad', emoji: '😢', label: 'Sad', color: '#f7b125' },
  { key: 'angry', emoji: '😡', label: 'Angry', color: '#e9710f' },
];
const REACTION_BY_KEY = REACTIONS.reduce((a, r) => { a[r.key] = r; return a; }, {});

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function Avatar({ name, src }) {
  const initials = (name || 'M').split(' ').map(x => x[0]).slice(0, 2).join('').toUpperCase();
  return <div className="chat-avatar" data-testid="feed-avatar">{src ? <img src={src} alt={name} /> : initials}</div>;
}

function renderWithMentions(text, mentions) {
  if (!text) return text;
  if (!mentions || !mentions.length) return text;
  const names = [...mentions].sort((a, b) => b.name.length - a.name.length).map(m => m.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`@(${names.join('|')})`, 'g');
  const parts = [];
  let last = 0, m;
  while ((m = pattern.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<strong key={m.index} className="feed-mention">{m[0]}</strong>);
    last = m.index + m[0].length;
  }
  parts.push(text.slice(last));
  return parts;
}

function MentionField({ tag, value, onChange, directory, placeholder, maxLength, rows, testId, inputProps }) {
  const ref = React.useRef(null);
  const [sugg, setSugg] = React.useState([]);
  const [matchStart, setMatchStart] = React.useState(null);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    const pos = e.target.selectionStart;
    const upTo = v.slice(0, pos);
    const m = upTo.match(/@([a-zA-Z0-9 ]{0,30})$/);
    if (m && (directory || []).length) {
      const q = m[1].toLowerCase();
      const matches = directory.filter(d => d.name.toLowerCase().startsWith(q)).slice(0, 5);
      setSugg(matches);
      setMatchStart(upTo.length - m[0].length);
    } else {
      setSugg([]);
      setMatchStart(null);
    }
  };

  const pick = (name) => {
    const pos = ref.current.selectionStart;
    const before = value.slice(0, matchStart);
    const after = value.slice(pos);
    const next = `${before}@${name} ${after}`;
    onChange(next);
    setSugg([]);
    setMatchStart(null);
    requestAnimationFrame(() => {
      if (!ref.current) return;
      ref.current.focus();
      const p = before.length + name.length + 2;
      ref.current.setSelectionRange(p, p);
    });
  };

  const Tag = tag;
  return (
    <div className="feed-mention-wrap">
      <Tag
        ref={ref}
        className={tag === 'textarea' ? 'feed-composer-input' : undefined}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={rows}
        data-testid={testId}
        {...(inputProps || {})}
      />
      {sugg.length > 0 && (
        <div className="feed-mention-dropdown" data-testid="feed-mention-dropdown">
          {sugg.map(m => (
            <button type="button" key={m.id} onClick={() => pick(m.name)} data-testid={`feed-mention-opt-${m.id}`}>{m.name}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function Composer({ onPost, directory }) {
  const [text, setText] = React.useState('');
  const [files, setFiles] = React.useState([]);
  const [busy, setBusy] = React.useState(false);

  const addFiles = (list) => {
    const arr = Array.from(list || []).slice(0, Math.max(0, 4 - files.length));
    setFiles(f => [...f, ...arr.map(file => ({ file, url: URL.createObjectURL(file), isVideo: file.type.startsWith('video/') }))].slice(0, 4));
  };
  const removeFile = (i) => setFiles(f => f.filter((_, x) => x !== i));

  const submit = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t && files.length === 0) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('text', t);
    files.forEach(f => fd.append('media', f.file));
    const ok = await onPost(fd);
    setBusy(false);
    if (ok) { setText(''); setFiles([]); }
  };

  return (
    <form className="feed-composer" onSubmit={submit} data-testid="feed-composer-form">
      <MentionField tag="textarea" value={text} onChange={setText} directory={directory}
                    placeholder="Got news, a win or a laugh? Drop it here…" maxLength={2000} rows={2} testId="feed-composer-input" />
      {files.length > 0 && (
        <div className="feed-composer-previews">
          {files.map((f, i) => (
            <div className="feed-composer-preview" key={i}>
              {f.isVideo ? <video src={f.url} muted /> : <img src={f.url} alt="" />}
              <button type="button" className="feed-composer-preview-x" onClick={() => removeFile(i)} data-testid={`feed-composer-remove-${i}`}><X size={12} /></button>
            </div>
          ))}
        </div>
      )}
      <div className="feed-composer-row">
        <label className="btn ghost feed-attach-btn" data-testid="feed-attach-btn">
          <input type="file" accept="image/*,video/*" multiple style={{ display: 'none' }}
                 onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} disabled={files.length >= 4} />
          <ImageIcon size={15} /> Photo/Video
        </label>
        <button className="btn" disabled={busy || (!text.trim() && files.length === 0)} data-testid="feed-post-submit">
          {busy ? 'Posting…' : 'Post'}
        </button>
      </div>
    </form>
  );
}

function CommentRow({ c, canDelete, onDelete }) {
  return (
    <div className="feed-comment" data-testid={`feed-comment-${c.id}`}>
      <Avatar name={c.authorName} src={c.authorAvatar} />
      <div className="feed-comment-body">
        <div><strong>{c.authorName}</strong><span>{timeAgo(c.createdAt)}</span></div>
        {c.text && <p>{renderWithMentions(c.text, c.mentions)}</p>}
        {c.gif && (
          <div className="feed-gif-comment" data-testid={`feed-comment-gif-${c.id}`}>
            <img src={c.gif.media_url} alt={c.gif.title || 'GIF'} />
          </div>
        )}
      </div>
      {canDelete && <button className="feed-comment-del" onClick={() => onDelete(c.id)} data-testid={`feed-comment-delete-${c.id}`}><X size={12} /></button>}
    </div>
  );
}

function ReactionButton({ post, onReact }) {
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [hoverKey, setHoverKey] = React.useState(null);
  const holdTimer = React.useRef(null);
  const closeTimer = React.useRef(null);
  const longPressed = React.useRef(false);
  const mine = REACTION_BY_KEY[post.myReaction];
  const total = post.reactionCount || 0;

  const openPicker = () => { clearTimeout(closeTimer.current); setPickerOpen(true); };
  const closePicker = () => { setPickerOpen(false); setHoverKey(null); };
  const scheduleClose = () => { closeTimer.current = setTimeout(closePicker, 350); };

  const pick = (key) => { onReact(post.id, key === post.myReaction ? null : key); closePicker(); };

  const onTouchStart = () => {
    longPressed.current = false;
    holdTimer.current = setTimeout(() => { longPressed.current = true; openPicker(); }, 300);
  };
  const onTouchMove = (e) => {
    if (!longPressed.current) return;
    const t = e.touches[0];
    const el = document.elementFromPoint(t.clientX, t.clientY);
    setHoverKey(el?.closest('[data-reaction-key]')?.getAttribute('data-reaction-key') || null);
  };
  const onTouchEnd = (e) => {
    clearTimeout(holdTimer.current);
    if (longPressed.current) {
      e.preventDefault();
      if (hoverKey) pick(hoverKey); else closePicker();
      longPressed.current = false;
    }
  };
  const quickToggle = () => {
    if (longPressed.current) return;
    onReact(post.id, post.myReaction ? null : 'like');
  };

  return (
    <div className="feed-reaction-wrap" onMouseEnter={openPicker} onMouseLeave={scheduleClose}
         onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
      {pickerOpen && (
        <div className="feed-reaction-picker" data-testid={`feed-reaction-picker-${post.id}`}
             onMouseEnter={openPicker} onMouseLeave={scheduleClose}>
          {REACTIONS.map(r => (
            <button key={r.key} type="button" data-reaction-key={r.key}
                    className={`feed-reaction-opt ${hoverKey === r.key ? 'hovered' : ''}`} title={r.label}
                    onClick={() => pick(r.key)} data-testid={`feed-reaction-opt-${post.id}-${r.key}`}>{r.emoji}</button>
          ))}
        </div>
      )}
      <button className={`feed-like-btn ${mine ? 'reacted' : ''}`} style={mine ? { color: mine.color } : undefined}
              onClick={quickToggle} data-testid={`feed-like-${post.id}`}>
        <span className="feed-reaction-emoji">{mine ? mine.emoji : '👍'}</span>
        {mine ? mine.label : 'Like'}
        {total > 0 && <span className="feed-reaction-count" data-testid={`feed-reaction-count-${post.id}`}>{total}</span>}
      </button>
    </div>
  );
}

function Post({ post, user, role, feedMediaUrl, directory, onReact, onComment, onDeleteComment, onDeletePost, onTogglePin }) {
  const [commentsOpen, setCommentsOpen] = React.useState(false);
  const [commentText, setCommentText] = React.useState('');
  const [commentGif, setCommentGif] = React.useState(null);
  const [gifOpen, setGifOpen] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const isAdmin = role === 'admin';
  const isAuthor = post.authorId === user?.id;
  const hasGiphy = !!process.env.REACT_APP_GIPHY_API_KEY;

  const submitComment = async (e) => {
    e.preventDefault();
    const t = commentText.trim();
    if (!t && !commentGif) return;
    setSending(true);
    await onComment(post.id, t, commentGif);
    setSending(false);
    setCommentText('');
    setCommentGif(null);
  };

  return (
    <div className={`card feed-post ${post.pinned ? 'feed-post-pinned' : ''}`} data-testid={`feed-post-${post.id}`}>
      {post.pinned && <span className="pill yellow feed-pinned-badge"><Pin size={11} /> Pinned</span>}
      <div className="feed-post-head">
        <Avatar name={post.authorName} src={post.authorAvatar} />
        <div className="feed-post-head-info">
          <strong>{post.authorName}</strong>
          <span>{post.authorIsAdmin && <span className="pill yellow" style={{ marginRight: 6, padding: '2px 7px', fontSize: 10 }}>Admin</span>}{timeAgo(post.createdAt)}</span>
        </div>
        <div className="feed-post-head-actions">
          {isAdmin && <button className="btn ghost feed-icon-btn" title={post.pinned ? 'Unpin' : 'Pin to top'} onClick={() => onTogglePin(post.id)} data-testid={`feed-pin-${post.id}`}><Pin size={14} /></button>}
          {(isAdmin || isAuthor) && <button className="btn ghost feed-icon-btn" title="Delete post" onClick={() => onDeletePost(post.id)} data-testid={`feed-delete-${post.id}`}><Trash2 size={14} /></button>}
        </div>
      </div>
      {post.text && <p className="feed-post-text" data-testid={`feed-post-text-${post.id}`}>{renderWithMentions(post.text, post.mentions)}</p>}
      {post.media && post.media.length > 0 && (
        <div className={`feed-media-grid feed-media-grid-${Math.min(post.media.length, 4)}`} data-testid={`feed-media-${post.id}`}>
          {post.media.map((m, i) => (
            <div className="feed-media-cell" key={i}>
              {m.type === 'video'
                ? <video src={feedMediaUrl(post.id, i)} controls />
                : <img src={feedMediaUrl(post.id, i)} alt="" />}
            </div>
          ))}
        </div>
      )}
      {post.gif && (
        <div className="feed-gif-post" data-testid={`feed-gif-post-${post.id}`}>
          <img src={post.gif.media_url} alt={post.gif.title || 'GIF'} />
          <span className="feed-gif-attrib">Powered by GIPHY</span>
        </div>
      )}
      <div className="feed-post-bar">
        <ReactionButton post={post} onReact={onReact} />
        <button className="feed-comment-btn" onClick={() => setCommentsOpen(o => !o)} data-testid={`feed-toggle-comments-${post.id}`}>
          <MessageCircle size={15} /> {post.comments.length > 0 ? `${post.comments.length} ${post.comments.length === 1 ? 'Comment' : 'Comments'}` : 'Comment'}
        </button>
      </div>
      {commentsOpen && (
        <div className="feed-comments" data-testid={`feed-comments-${post.id}`}>
          {post.comments.map(c => (
            <CommentRow key={c.id} c={c} canDelete={isAdmin || c.authorId === user?.id} onDelete={(cid) => onDeleteComment(post.id, cid)} />
          ))}
          {commentGif && (
            <div className="feed-composer-previews">
              <div className="feed-composer-preview">
                <img src={commentGif.preview_url || commentGif.media_url} alt="" />
                <button type="button" className="feed-composer-preview-x" onClick={() => setCommentGif(null)} data-testid={`feed-comment-remove-gif-${post.id}`}><X size={12} /></button>
              </div>
            </div>
          )}
          <form className="feed-comment-form" onSubmit={submitComment}>
            <MentionField tag="input" value={commentText} onChange={setCommentText} directory={directory}
                          placeholder="Write a comment…" maxLength={1000} testId={`feed-comment-input-${post.id}`} />
            {hasGiphy && (
              <button type="button" className="feed-comment-gif-btn" onClick={() => setGifOpen(true)} data-testid={`feed-comment-gif-btn-${post.id}`}>
                <Smile size={16} />
              </button>
            )}
            <button type="submit" disabled={sending || (!commentText.trim() && !commentGif)} data-testid={`feed-comment-submit-${post.id}`}><Send size={14} /></button>
          </form>
          {gifOpen && <GifPicker onSelect={(g) => { setCommentGif(g); setGifOpen(false); }} onClose={() => setGifOpen(false)} />}
        </div>
      )}
    </div>
  );
}

export default function Feed() {
  const { user, role, getFeed, createFeedPost, deleteFeedPost, toggleFeedPin, reactFeedPost, addFeedComment, deleteFeedComment, feedMediaUrl, markFeedSeen, getMembersDirectory } = useSpc();
  const [posts, setPosts] = React.useState(null);
  const [directory, setDirectory] = React.useState([]);

  React.useEffect(() => {
    getFeed().then(setPosts);
    markFeedSeen();
    getMembersDirectory().then(r => setDirectory(r.data.members || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const patchPost = (updated) => {
    if (!updated) return;
    setPosts(list => (list || []).map(p => p.id === updated.id ? updated : p));
  };

  const onPost = async (formData) => {
    const data = await createFeedPost(formData);
    if (data && data.post) { setPosts(list => [data.post, ...(list || [])]); return true; }
    return false;
  };
  const onDeletePost = async (id) => {
    if (!window.confirm('Remove this post?')) return;
    const ok = await deleteFeedPost(id);
    if (ok) setPosts(list => (list || []).filter(p => p.id !== id));
  };
  const onTogglePin = async (id) => patchPost(await toggleFeedPin(id));
  const onReact = async (id, type) => patchPost(await reactFeedPost(id, type));
  const onComment = async (id, text, gif) => patchPost(await addFeedComment(id, text, gif));
  const onDeleteComment = async (pid, cid) => patchPost(await deleteFeedComment(pid, cid));

  const list = posts || [];
  return (
    <>
      <div className="section-head">
        <div><div className="eyebrow">Club Feed</div><h2>The Clubhouse Wall</h2><p>Every post, win and shout-out — together in one place, built for SPC.</p></div>
      </div>
      <Composer onPost={onPost} directory={directory} />
      {posts === null ? (
        <div className="empty" data-testid="feed-loading">Loading the feed…</div>
      ) : list.length === 0 ? (
        <div className="empty" data-testid="feed-empty">No posts yet — be the first to share something with the club.</div>
      ) : (
        <div className="feed-list" data-testid="feed-list">
          {list.map(p => (
            <Post key={p.id} post={p} user={user} role={role} feedMediaUrl={feedMediaUrl} directory={directory}
                  onReact={onReact} onComment={onComment} onDeleteComment={onDeleteComment}
                  onDeletePost={onDeletePost} onTogglePin={onTogglePin} />
          ))}
        </div>
      )}
    </>
  );
}
